// Utilitaires de scoring (gamification) — fonctions PURES, sans réseau ni DOM,
// pour rester testables avec un simple `node`. Toute la logique de points,
// niveaux, plafonds anti-spam et progression d'objectifs vit ici.

// Points attribués par type d'action.
export const POINTS = {
  debrief: 10,
  comment: 3,
  reaction: 1,
  quiz_entry: 5
};

// Plafonds journaliers anti-spam : nombre d'évènements *comptés* par jour et par
// type. Au-delà, les actions restent possibles mais ne rapportent plus de points
// ce jour-là. Ces plafonds sont affichés dans l'UI pour rester transparents.
export const DAILY_CAPS = {
  debrief: 5,
  comment: 10,
  reaction: 20,
  quiz_entry: 10
};

// Objectifs préréglés proposés à l'utilisateur. `kind` relie l'objectif au type
// d'évènement compté ; `period` borne la fenêtre de calcul de la progression.
export const GOAL_PRESETS = [
  { id: 'analyses_week', label: tr('Analyses par semaine', 'Analyses per week'), kind: 'debrief', period: 'week', defaultTarget: 3 },
  { id: 'helpful_comments', label: tr('Commentaires utiles (semaine)', 'Useful comments (week)'), kind: 'comment', period: 'week', defaultTarget: 10 },
  { id: 'quiz_day', label: tr('Quiz par jour', 'Quiz per day'), kind: 'quiz_entry', period: 'day', defaultTarget: 1 }
];

export function getGoalPreset(id) {
  return GOAL_PRESETS.find((p) => p.id === id) || null;
}

export function pointsForKind(kind) {
  return POINTS[kind] || 0;
}

// Points « de base » à vie, estimés à partir des compteurs de contenu existant.
// Sert d'affichage tant que la table score_events n'est pas alimentée.
export function computeBasePoints({ debriefs = 0, comments = 0, reactions = 0, quizzes = 0 } = {}) {
  return (
    Math.max(0, debriefs) * POINTS.debrief +
    Math.max(0, comments) * POINTS.comment +
    Math.max(0, reactions) * POINTS.reaction +
    Math.max(0, quizzes) * POINTS.quiz_entry
  );
}

// Points d'un type sur une journée, plafonné par DAILY_CAPS (anti-spam).
export function cappedDailyPoints(kind, count) {
  const cap = DAILY_CAPS[kind] ?? Infinity;
  const counted = Math.min(Math.max(0, Number(count) || 0), cap);
  return counted * pointsForKind(kind);
}

// Niveau à partir du total de points. Courbe douce : palier ~50*n².
// Niveau 1 dès 0 pt, niveau 2 à 50 pts, niveau 3 à 200 pts, etc.
export function levelForPoints(points) {
  const p = Math.max(0, Number(points) || 0);
  return Math.floor(Math.sqrt(p / 50)) + 1;
}

// Points nécessaires pour atteindre un niveau donné (inverse de levelForPoints).
export function pointsForLevel(level) {
  const n = Math.max(1, Math.floor(Number(level) || 1));
  return 50 * (n - 1) * (n - 1);
}

// Progression vers le prochain niveau : {level, current, next, pct}.
export function levelProgress(points) {
  const p = Math.max(0, Number(points) || 0);
  const level = levelForPoints(p);
  const floor = pointsForLevel(level);
  const ceil = pointsForLevel(level + 1);
  const span = Math.max(1, ceil - floor);
  const pct = Math.min(100, Math.round(((p - floor) / span) * 100));
  return { level, current: p, floorPoints: floor, nextPoints: ceil, pct };
}

// Progression d'un objectif : compare un compteur courant à la cible.
export function goalProgress(goal, current) {
  const target = Math.max(1, Number(goal?.target) || 1);
  const cur = Math.max(0, Number(current) || 0);
  const pct = Math.min(100, Math.round((cur / target) * 100));
  return { current: cur, target, pct, done: cur >= target };
}
import { tr } from '../services/i18n.js';
